import * as _ from 'lodash';
import type { Column } from 'react-table';

import { useDialogContext } from '../../../../contexts/DialogContext';
import { BUTTON_TYPE } from '../../../generic/Button/button-constants';
import { icons } from '../../../generic/Icon/icons';
import SimpleReactTable, { MINIMUM_PAGE_SIZE } from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import SettingsTableRowActions from '../../shared/SettingsTableRowActions';
import type { MinimalOrganizationAccessRequest } from '../../../../../shared/organizationV2';

type OrganizationAccessRequestsTableProps = {
  accessRequests: MinimalOrganizationAccessRequest[];
  searchQuery: string;
  onAcceptRequest(accessRequestId: number): void;
  onIgnoreRequest(accessRequestId: number): void;
};

const OrganizationAccessRequestsTable = (props: OrganizationAccessRequestsTableProps) => {
  const { onConfirm } = useDialogContext();

  let filteredRequests = props.accessRequests;
  if (props.searchQuery) {
    filteredRequests = filteredRequests.filter(
      (request) => request.email.toLowerCase().includes(props.searchQuery.toLowerCase()),
    );
  }

  const acceptRequestFn = (request: MinimalOrganizationAccessRequest) => () =>
    onConfirm({
      title: 'Confirm Accept request',
      body: (
        <>
          <p>Are you sure you want to accept this access request?</p>
          <p>This will send an invite email to &ldquo;{request.email}&rdquo; to join your organization in FOSSA.</p>
        </>
      ),
      onConfirm: () => props.onAcceptRequest(request.id),
      confirmBtnType: BUTTON_TYPE.PRIMARY,
    });

  const ignoreRequestFn = (request: MinimalOrganizationAccessRequest) => () =>
    onConfirm({
      title: `${request.fulfilled ? 'Remove' : 'Ignore'} request?`,
      body: (
        <p>
          Are you sure you want to {request.fulfilled ? 'remove' : 'ignore'} this access request?
        </p>
      ),
      onConfirm: () => props.onIgnoreRequest(request.id),
    });

  const columns: Column[] = [
    {
      id: 'email',
      Header: (
        <TableHeader title="Requester’s Email" />
      ),
      accessor: 'email',
      resizable: false,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: { original: MinimalOrganizationAccessRequest }) => (
        <span>{original.email}{original.fulfilled && ' (Invite sent)'}</span>
      ),
      style: {
        overflow: 'visible',
        paddingRight: '15px',
      },
    },
    {
      id: 'actions',
      Header: <TableHeader title="Actions" />,
      accessor: 'actions',
      resizable: false,
      sortable: false,
      width: 70,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: { original: MinimalOrganizationAccessRequest }) => (
        <SettingsTableRowActions>
          <SettingsTableRowActions.IconButton
            tooltipId={`${original.email}-actions`}
            tooltip={!original.fulfilled ? 'Invite Sent' : 'Invite User'}
            isDisabled={!original.fulfilled}
            icon={icons.envelopeRegular}
            onClick={acceptRequestFn(original)}
          />
          <SettingsTableRowActions.IconButton
            tooltipId={`${original.email}-actions`}
            tooltip={original.fulfilled ? 'Remove Request' : 'Ignore Request'}
            icon={icons.trashRegular}
            onClick={ignoreRequestFn(original)}
          />
        </SettingsTableRowActions>
      ),
    },
  ];

  return (
    <SimpleReactTable
      data={filteredRequests}
      columns={columns}
      defaultSortMode={[{ id: 'email', desc: false }]}
      onSortedChanged={_.noop}
      showPagination={filteredRequests.length > MINIMUM_PAGE_SIZE}
      cacheId="organizationAccessRequests"
    />
  );
};

export default OrganizationAccessRequestsTable;
